/** WATCHLIST — securities followed without being held, on named lists. */
import { useCallback, useMemo } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { GET_USER_WATCHLIST, GET_USER_WATCHLIST_NAMES } from '../queries';
import {
  ADD_TO_WATCHLIST, CREATE_WATCHLIST, DELETE_WATCHLIST, REMOVE_FROM_WATCHLIST, RENAME_WATCHLIST, UPDATE_WATCHLIST_ITEM,
} from '../mutations';
import { WATCH_TYPES, watchType } from '../../lib/security';
import { EMPTY } from './_shared';

/**
 * The register's `securityType` vocabulary, re-exported so the screens that
 * already import it from here keep working. It is defined in `lib/security`
 * because the `agent` slice validates against the same words and cannot import
 * a data hook. `watchType` is applied to every write and every `has()` lookup,
 * so a caller still saying `mutual_fund` reaches the right rows instead of
 * being refused by the server.
 */
export { WATCH_TYPES, watchType };

/** The list every item lands on when none is named (the backend's DEFAULT_WATCHLIST_NAME). */
export const DEFAULT_WATCHLIST = 'General';

/** A list name as the backend keeps it: trimmed, the default when blank. */
export const listNameOf = (name) => (String(name ?? '').trim() || DEFAULT_WATCHLIST);

/* Every write re-reads the lists' names too, so a list that just gained its
   first name, or lost its last, shows up or goes. */
const REFETCH_NAMES = { refetchQueries: [GET_USER_WATCHLIST_NAMES] };

/**
 * One watchlist (`listName`), or every list when none is named, and the
 * writes that change it.
 *
 * The mutations answer `{ success, message }` rather than the row they wrote,
 * so each write re-reads the list — the alternative is constructing a cache
 * entry from arguments the backend enriches (price, day move, sector), which
 * would show a half-built row until the next refresh.
 *
 * `has` is the whole point of exposing the set: every "add to watchlist"
 * button needs to know whether it is an add or a remove, and asking per row
 * is one pass over a list that is already in memory. Each write takes an
 * optional `{ listName }` that outranks the hook's: a button on a fund page
 * follows the name on whichever list the person picked.
 */
export function useWatchlist({ skip = false, listName = null } = {}) {
  const list = listName == null || listName === '' ? null : listNameOf(listName);
  const { data, loading, error, refetch } = useQuery(GET_USER_WATCHLIST, {
    variables: { language: null, listName: list },
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const [addMutation, { loading: adding }] = useMutation(ADD_TO_WATCHLIST, REFETCH_NAMES);
  const [removeMutation, { loading: removing }] = useMutation(REMOVE_FROM_WATCHLIST, REFETCH_NAMES);
  const [updateMutation, { loading: updating }] = useMutation(UPDATE_WATCHLIST_ITEM);

  const items = data?.getUserWatchlist ?? EMPTY;

  /* Keyed by symbol AND type: the register holds a scheme and a portfolio
     separately even where the symbol looks the same. */
  const keys = useMemo(
    () => new Set(items.map((i) => `${i.securityType}:${i.securitySymbol}`)),
    [items],
  );

  const has = useCallback(
    (securitySymbol, securityType = WATCH_TYPES.fund) => keys.has(`${watchType(securityType)}:${securitySymbol}`),
    [keys],
  );

  const onList = useCallback((opts) => (opts?.listName != null && opts.listName !== '' ? listNameOf(opts.listName) : list), [list]);

  const add = useCallback(async (securitySymbol, securityType = WATCH_TYPES.fund, limits = {}, opts = {}) => {
    const res = await addMutation({
      variables: {
        securitySymbol,
        securityType: watchType(securityType),
        upperPriceLimit: limits.upper ?? null,
        lowerPriceLimit: limits.lower ?? null,
        listName: onList(opts),
      },
    });
    await refetch();
    return res?.data?.addToWatchlist ?? null;
  }, [addMutation, refetch, onList]);

  const remove = useCallback(async (securitySymbol, securityType = WATCH_TYPES.fund, opts = {}) => {
    const res = await removeMutation({ variables: { securitySymbol, securityType: watchType(securityType), listName: onList(opts) } });
    await refetch();
    return res?.data?.removeFromWatchlist ?? null;
  }, [removeMutation, refetch, onList]);

  const update = useCallback(async (securitySymbol, securityType, limits = {}, opts = {}) => {
    const res = await updateMutation({
      variables: {
        securitySymbol,
        securityType: watchType(securityType),
        upperPriceLimit: limits.upper ?? null,
        lowerPriceLimit: limits.lower ?? null,
        listName: onList(opts),
      },
    });
    await refetch();
    return res?.data?.updateWatchlistItem ?? null;
  }, [updateMutation, refetch, onList]);

  /** One flag for every write, because one button fires any of them. */
  const busy = adding || removing || updating;

  return useMemo(
    () => ({ listName: list, items, loading, error, refetch, has, add, remove, update, busy }),
    [list, items, loading, error, refetch, has, add, remove, update, busy],
  );
}

/**
 * The user's named lists — the default list first, each with its count —
 * and the writes on the lists themselves: `create(name)`, `rename(old, new)`,
 * `remove(name)`. Each resolves to `{ success, message }`.
 */
export function useWatchlistNames({ skip = false } = {}) {
  const { data, loading, error, refetch } = useQuery(GET_USER_WATCHLIST_NAMES, {
    skip,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });
  const [createMutation, { loading: creating }] = useMutation(CREATE_WATCHLIST, REFETCH_NAMES);
  const [renameMutation, { loading: renaming }] = useMutation(RENAME_WATCHLIST, { refetchQueries: [GET_USER_WATCHLIST_NAMES, GET_USER_WATCHLIST] });
  const [deleteMutation, { loading: deleting }] = useMutation(DELETE_WATCHLIST, { refetchQueries: [GET_USER_WATCHLIST_NAMES, GET_USER_WATCHLIST] });

  const names = data?.getUserWatchlistNames ?? EMPTY;
  const create = useCallback(async (name) => {
    const res = await createMutation({ variables: { listName: listNameOf(name) } });
    return res?.data?.createWatchlist ?? null;
  }, [createMutation]);
  const rename = useCallback(async (oldName, newName) => {
    const res = await renameMutation({ variables: { oldName: listNameOf(oldName), newName: listNameOf(newName) } });
    return res?.data?.renameWatchlist ?? null;
  }, [renameMutation]);
  const remove = useCallback(async (name) => {
    const res = await deleteMutation({ variables: { listName: listNameOf(name) } });
    return res?.data?.deleteWatchlist ?? null;
  }, [deleteMutation]);

  return useMemo(() => ({
    names,
    /* For a picker: the lists by name, the default first as the backend orders them. */
    options: names.map((n) => ({ value: n.name, label: n.count ? `${n.name} (${n.count})` : n.name })),
    loading, error, refetch, create, rename, remove,
    busy: creating || renaming || deleting,
  }), [names, loading, error, refetch, create, rename, remove, creating, renaming, deleting]);
}
